import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

export interface CustomCheckBoxProps {
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}

const CustomCheckBox: React.FC<CustomCheckBoxProps> = ({
  checked = false,
  onChange,
  onClick,
  ariaLabel = 'Select study',
  className = '',
  disabled = false
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(checked);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
    if (onChange) {
      onChange(e);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <label
      onClick={e => e.stopPropagation()}
      className={classnames(
        'relative inline-flex items-center justify-center select-none group',
        className,
        { 'cursor-not-allowed': disabled }
      )}
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleChange}
        onClick={handleClick}
        aria-label={ariaLabel}
        className="sr-only peer"
        disabled={disabled}
      />
      <div
        title={ariaLabel}
        className={classnames(
          'w-4 h-4 rounded-[4px] flex items-center justify-center transition-all duration-200',
          'border border-white/20 bg-[#080c18]/90',
          'group-hover:border-indigo-400/80 group-hover:bg-indigo-500/15 group-hover:shadow-[0_0_8px_rgba(99,102,241,0.25)]',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-400 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-[#080c18]',
          {
            '!bg-gradient-to-br !from-blue-500 !to-indigo-600 !border-indigo-400 shadow-[0_0_10px_rgba(99,102,241,0.4)]': isChecked,
            'opacity-40 cursor-not-allowed': disabled,
          }
        )}
      >
        <svg
          className={classnames(
            'w-2.5 h-2.5 text-white transition-all duration-200 ease-out transform',
            isChecked ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          )}
          viewBox="0 0 12 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1.5 5 4.5 8 10.5 2" />
        </svg>
      </div>
    </label>
  );
};

CustomCheckBox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default CustomCheckBox;
